import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Card, Chip, IconButton, CardContent, Grid, Paper, Container, Tooltip as TooltipCustom, useMediaQuery, Backdrop, CircularProgress, useTheme, Stack } from '@mui/material';
import { Menu as MenuIcon, Help as HelpIcon } from '@mui/icons-material';
// app UI controller for toggling sidenav
import { useMaterialUIController, setMiniSidenav } from "context";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import SouthIcon from '@mui/icons-material/South';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import {
  ComposableMap,
  Geographies,
  Geography
} from "react-simple-maps";
import FilterPanelContainer from "components/Filter/Filterpopup"
import KpiMetricscards from "components/KPI/KpiMetricscards";
import TimeSeriescards from "components/TimeSeries/TimeSeriescards";
import HighlightsCards from "components/Highlights/Highlightscard";
import Visualizations from "components/Visualizations/Visualizations";
import PieLegendCard from "components/Visualizations/Piecharts"
import Barcharts from "components/Visualizations/Barcharts"
import LineChartSubBrand from "components/Visualizations/Linechartsubbrand";
import LineChartsale from "components/Visualizations/LineChartsale"
import InventoryCharts from "components/Visualizations/Inventorycharts";
import WaterfallChart from "components/Visualizations/waterfallchart";
import TopSkuBarChart from "components/Visualizations/SkuBarchart";
import MultiBarcharts from "components/Visualizations/MultiBarcharts";
import datadrilldown from "assets/images/selectedicons/DataDrillDown.svg";
import drillDownArrow from "assets/images/selectedicons/DrillDownArrow.svg";
import datadrillhover from 'assets/images/selectedicons/Data Drill Down1.svg';
import FilterChip from "components/FilterChip/FilterChip";
import { fetchOperations } from "services/operations.service"
import { getTimeSeriesLabels } from "../../../components/TimeSeries/monthUtils";
import { useFilterContext } from "../../../../src/context/FilterContext";
import { fetchOperationsFilter } from "../../../services/operationsFilter.service";

const OperationsDashboard = ({ subcategoryToken: propSubcategoryToken }) => {
  const {
    lastYearSameMonth,
    currentMonthLabel,
    prevMonthLabel
  } = getTimeSeriesLabels();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Below 900px
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // Between 900px and 1200px
  // State for API data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operationsData, setOperationsData] = useState(null);

  // Filter state for Highlights section time period chips
  const [highlightsActiveIdx, setHighlightsActiveIdx] = React.useState(0);
  const [highlightsActiveChipName, setHighlightsActiveChipName] = useState("YTD");

  // Filter state for Visualizations section time period chips
  const [visualizationActiveIdx, setVisualizationActiveIdx] = React.useState(0);
  const [visualizationActiveChipName, setVisualizationActiveChipName] = useState("YTD");
  const { setFilterOptions, setFilterChips, filterOptions, setActiveFilters, activeFilters } = useFilterContext();
  const [myDashboardfilterData, setMyDashboardfilterData] = useState(null);

  useEffect(() => {
    const header = document.querySelector('.MuiAppBar-root');
    if (header) {
      header.style.display = 'none';
    }
  }, []);



  // Helper function to build GraphQL filters from activeFilters
  const buildGqlFiltersFromActive = useCallback((filters) => {
    const extract = (items) => {
      if (!items || !Array.isArray(items) || items.length === 0) return null;
      // Handle both string arrays and object arrays with name property
      const names = items.map(item => typeof item === 'string' ? item : item?.name).filter(Boolean);
      return names.length > 0 ? { in: names } : null;
    };

    const gqlFiltersRaw = {
      companyName: extract(filters?.Company),
      categoryName: extract(filters?.Category),
      brandName: extract(filters?.Brand),
      subBrandSubcategory: extract(filters?.["Sub-Brand"]),
      subBrandName: extract(filters?.["Sub-Brand"]),
      routeToMarket: extract(filters?.["Route to Market"]),
      channel: extract(filters?.Channel)
    };
    // Remove null/undefined values to avoid backend errors
    return Object.fromEntries(
      Object.entries(gqlFiltersRaw).filter(([_, value]) => value !== null && value !== undefined)
    );
  }, []);

  // Fetch filter options on initial load
  useEffect(() => {
    const fetchFilterOptions = async () => {
      if (filterOptions && Object.keys(filterOptions).length > 0) {
        return; // Already have filter options
      }

      try {
        const operationsFilterResponse = await fetchOperationsFilter();
        const filterOptionsData = {
          Company: operationsFilterResponse?.companies?.items || [],
          Category: operationsFilterResponse?.categories?.items || [],
          Brand: operationsFilterResponse?.brands?.items || [],
          "Sub-Brand": operationsFilterResponse?.subBrands?.items || [],
          "Route to Market": operationsFilterResponse?.routeToMarkets?.items || [],
          Channel: operationsFilterResponse?.channels?.items || [],
        };
        setFilterOptions(filterOptionsData);
        setMyDashboardfilterData(operationsFilterResponse);

        const companyChips = operationsFilterResponse?.companies.items || [];
        const categoryChips = operationsFilterResponse?.categories?.items || [];
        setFilterChips([
          { key: "all", name: "All" },
          ...companyChips,
          ...categoryChips,
          { key: "custom", name: "Custom" }
        ]);
      } catch (error) {
        console.error('Failed loading filter options:', error);
      }
    };

    fetchFilterOptions();
  }, [filterOptions, setFilterOptions, setFilterChips]);

  // Fetch dashboard data when filters or time periods change
  useEffect(() => {
    const fetchAllData = async () => {
      // Wait for filter options to be available if they're not loaded yet
      if (!filterOptions || Object.keys(filterOptions).length === 0) {
        return;
      }

      try {
        setLoading(true);

        // Build GraphQL filters from activeFilters if they exist and have selections
        // Otherwise, use all available filter options
        let gqlFilters = {};

        if (activeFilters && Object.values(activeFilters).some(arr => (arr || []).length > 0)) {
          // Use activeFilters when they have selections
          gqlFilters = buildGqlFiltersFromActive(activeFilters);
        } else {
          // Fallback to all filter options if no active filters
          const extractNames = (items) => {
            if (!items?.length) return null;
            return {
              in: items.map(item => typeof item === 'string' ? item : item?.name).filter(Boolean)
            };
          };

          const gqlFiltersRaw = {
            companyName: extractNames(filterOptions.Company),
            categoryName: extractNames(filterOptions.Category),
            brandName: extractNames(filterOptions.Brand),
            subBrandSubcategory: extractNames(filterOptions["Sub-Brand"]),
            subBrandName: extractNames(filterOptions?.["Sub-Brand"]),
            routeToMarket: extractNames(filterOptions["Route to Market"]),
            channel: extractNames(filterOptions.Channel)
          };
          gqlFilters = Object.fromEntries(
            Object.entries(gqlFiltersRaw).filter(([_, value]) => value !== null && value !== undefined)
          );
        }

        console.log('Fetching dashboard data with filters:', gqlFilters);
        const myDashboardResponse = await fetchOperations(
          visualizationActiveChipName,
          highlightsActiveChipName,
          gqlFilters
        );

        const myDashboardItem = myDashboardResponse || null;
        setOperationsData(myDashboardItem);

      } catch (error) {
        console.error('Failed loading operations dashboard data:', error);
        setError('Failed to load operations dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [location.search, propSubcategoryToken, visualizationActiveChipName, highlightsActiveChipName, activeFilters, buildGqlFiltersFromActive, filterOptions]);



  // console.log(operationsData?.stockAmountCases?.items.currentMonth, "operationsData Reposnse")

  const formatToMillions = (value) => {
    return value == null || value === "-"
      ? "-"
      : (Number(value) / 1_000_000).toFixed(1);
  };




  const truncateDecimal = (num, decimals = 1) => {
    const factor = Math.pow(10, decimals);
    return Math.floor(num * factor) / factor;
  };

  console.log(`$${(formatToMillions(operationsData?.perfectOrderRates?.items.currentMonth))}M`, operationsData?.perfectOrderRates?.items.currentMonth
    , "Formatted Stock Amount Cases Current Month")
  // KPIs cards section data  stockAmountCases
  const Kpiscardsdata = [
    {
      title: "Stock Amount (Cases)",
      values: [
        {
          label: "MTD",
          change: operationsData?.stockAmountCases?.items?.cMvsGoal ? `${operationsData?.stockAmountCases?.items?.cMvsGoal}%` : "0%",
          changely: operationsData?.stockAmountCases?.items?.cMvsLM ? `${operationsData?.stockAmountCases?.items?.cMvsLM}%` : "0%",
          value: `$${formatToMillions(operationsData?.stockAmountCases?.items.currentMonth)}M`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(operationsData?.stockAmountCases?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(operationsData?.stockAmountCases?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: operationsData?.stockAmountCases?.items?.cQvsGoal ? `${operationsData?.stockAmountCases?.items?.cQvsGoal}%` : "0%",
          changely: operationsData?.stockAmountCases?.items?.cQvsLQ ? `${operationsData?.stockAmountCases?.items?.cQvsLQ}%` : "0%",
          value: `$${formatToMillions(operationsData?.stockAmountCases?.items?.currentQuarter)}M`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(operationsData?.stockAmountCases?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(operationsData?.stockAmountCases?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: operationsData?.stockAmountCases?.items?.cYvsGoal ? `${operationsData?.stockAmountCases?.items?.cYvsGoal}%` : "0%",
          changely: operationsData?.stockAmountCases?.items?.cYvsLY ? `${operationsData?.stockAmountCases?.items?.cYvsLY}%` : "0%",
          value: `$${formatToMillions(operationsData?.stockAmountCases?.items?.currentYear)}M`,
          status: "increase",
          title: "Year",
          lyValue: `$${formatToMillions(operationsData?.stockAmountCases?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(operationsData?.stockAmountCases?.items?.goalCurrentYear)}M`
        }
      ]
    },
    {
      title: "Stock Amount (Dollars)",
      values: [
        {
          label: "MTD",
          change: operationsData?.stockAmountDollars?.items?.cMvsGoal ? `${operationsData?.stockAmountDollars?.items?.cMvsGoal}%` : "0%",
          changely: operationsData?.stockAmountDollars?.items?.cMvsLM ? `${operationsData?.stockAmountDollars?.items?.cMvsLM}%` : "0%",
          value: `$${formatToMillions(operationsData?.stockAmountDollars?.items.currentMonth)}M`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(operationsData?.stockAmountDollars?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(operationsData?.stockAmountDollars?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: operationsData?.stockAmountDollars?.items?.cQvsGoal ? `${operationsData?.stockAmountDollars?.items?.cQvsGoal}%` : "0%",
          changely: operationsData?.stockAmountDollars?.items?.cQvsLQ ? `${operationsData?.stockAmountDollars?.items?.cQvsLQ}%` : "0%",
          value: `$${formatToMillions(operationsData?.stockAmountDollars?.items?.currentQuarter)}M`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(operationsData?.stockAmountDollars?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(operationsData?.stockAmountDollars?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: operationsData?.stockAmountDollars?.items?.cYvsGoal ? `${operationsData?.stockAmountDollars?.items?.cYvsGoal}%` : "0%",
          changely: operationsData?.stockAmountDollars?.items?.cYvsLY ? `${operationsData?.stockAmountDollars?.items?.cYvsLY}%` : "0%",
          value: `$${formatToMillions(operationsData?.stockAmountDollars?.items?.currentYear)}M`,
          status: "increase",
          title: "Year",
          lyValue: `$${formatToMillions(operationsData?.stockAmountDollars?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(operationsData?.stockAmountDollars?.items?.goalCurrentYear)}M`
        }
      ]
    },
    {
      title: "Inventory Turnover (Units)",
      values: [
        {
          label: "MTD",
          change: operationsData?.inventoryTurnOverUnits?.items?.cMvsGoal ? `${operationsData?.inventoryTurnOverUnits?.items?.cMvsGoal}%` : "0%",
          changely: operationsData?.inventoryTurnOverUnits?.items?.cMvsLM ? `${operationsData?.inventoryTurnOverUnits?.items?.cMvsLM}%` : "0%",
          value: `$${formatToMillions(operationsData?.inventoryTurnOverUnits?.items.currentMonth)}M`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(operationsData?.inventoryTurnOverUnits?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(operationsData?.inventoryTurnOverUnits?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: operationsData?.inventoryTurnOverUnits?.items?.cQvsGoal ? `${operationsData?.inventoryTurnOverUnits?.items?.cQvsGoal}%` : "0%",
          changely: operationsData?.inventoryTurnOverUnits?.items?.cQvsLQ ? `${operationsData?.inventoryTurnOverUnits?.items?.cQvsLQ}%` : "0%",
          value: `$${formatToMillions(operationsData?.inventoryTurnOverUnits?.items?.currentQuarter)}M`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(operationsData?.inventoryTurnOverUnits?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(operationsData?.inventoryTurnOverUnits?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: operationsData?.inventoryTurnOverUnits?.items?.cYvsGoal ? `${operationsData?.inventoryTurnOverUnits?.items?.cYvsGoal}%` : "0%",
          changely: operationsData?.inventoryTurnOverUnits?.items?.cYvsLY ? `${operationsData?.inventoryTurnOverUnits?.items?.cYvsLY}%` : "0%",
          value: `$${formatToMillions(operationsData?.inventoryTurnOverUnits?.items?.currentYear)}M`,
          status: "increase",
          title: "Year",
          lyValue: `$${formatToMillions(operationsData?.inventoryTurnOverUnits?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(operationsData?.inventoryTurnOverUnits?.items?.goalCurrentYear)}M`
        }
      ]
    },
    {
      title: "Perfect Order Rate (OTIF)",
      values: [
        {
          label: "MTD",
          change: operationsData?.perfectOrderRates?.items?.cMvsGoal ? `${operationsData?.perfectOrderRates?.items?.cMvsGoal}%` : "0%",
          changely: operationsData?.perfectOrderRates?.items?.cMvsLM ? `${operationsData?.perfectOrderRates?.items?.cMvsLM}%` : "0%",
          value: `$${formatToMillions(operationsData?.perfectOrderRates?.items.currentMonth)}M`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(operationsData?.perfectOrderRates?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(operationsData?.perfectOrderRates?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: operationsData?.perfectOrderRates?.items?.cQvsGoal ? `${operationsData?.perfectOrderRates?.items?.cQvsGoal}%` : "0%",
          changely: operationsData?.perfectOrderRates?.items?.cQvsLQ ? `${operationsData?.perfectOrderRates?.items?.cQvsLQ}%` : "0%",
          value: `$${formatToMillions(operationsData?.perfectOrderRates?.items?.currentQuarter)}M`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(operationsData?.perfectOrderRates?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(operationsData?.perfectOrderRates?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: operationsData?.perfectOrderRates?.items?.cYvsGoal ? `${operationsData?.perfectOrderRates?.items?.cYvsGoal}%` : "0%",
          changely: operationsData?.perfectOrderRates?.items?.cYvsLY ? `${operationsData?.perfectOrderRates?.items?.cYvsLY}%` : "0%",
          value: `$${formatToMillions(operationsData?.perfectOrderRates?.items?.currentYear)}M`,
          status: "decrease",
          title: "Year",
          lyValue: `$${formatToMillions(operationsData?.perfectOrderRates?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(operationsData?.perfectOrderRates?.items?.goalCurrentYear)}M`
        }
      ]
    },
    {
      title: "On Time Delivery (OT)",
      values: [
        {
          label: "MTD",
          change: operationsData?.onTimeDeliveryRates?.items?.cMvsGoal ? `${operationsData?.onTimeDeliveryRates?.items?.cMvsGoal}%` : "0%",
          changely: operationsData?.onTimeDeliveryRates?.items?.cMvsLM ? `${operationsData?.onTimeDeliveryRates?.items?.cMvsLM}%` : "0%",
          value: `$${formatToMillions(operationsData?.onTimeDeliveryRates?.items.currentMonth)}M`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(operationsData?.onTimeDeliveryRates?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(operationsData?.onTimeDeliveryRates?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: operationsData?.onTimeDeliveryRates?.items?.cQvsGoal ? `${operationsData?.onTimeDeliveryRates?.items?.cQvsGoal}%` : "0%",
          changely: operationsData?.onTimeDeliveryRates?.items?.cQvsLQ ? `${operationsData?.onTimeDeliveryRates?.items?.cQvsLQ}%` : "0%",
          value: `$${formatToMillions(operationsData?.onTimeDeliveryRates?.items?.currentQuarter)}M`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(operationsData?.onTimeDeliveryRates?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(operationsData?.onTimeDeliveryRates?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: operationsData?.perfectOrderRates?.items?.cYvsGoal ? `${operationsData?.perfectOrderRates?.items?.cYvsGoal}%` : "0%",
          changely: operationsData?.perfectOrderRates?.items?.cYvsLY ? `${operationsData?.perfectOrderRates?.items?.cYvsLY}%` : "0%",
          value: `$${formatToMillions(operationsData?.perfectOrderRates?.items?.currentYear)}M`,
          status: "decrease",
          title: "Year",
          lyValue: `$${formatToMillions(operationsData?.perfectOrderRates?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(operationsData?.perfectOrderRates?.items?.goalCurrentYear)}M`
        }
      ]
    },
    {
      title: "Order Fill Rate (IF)",
      values: [
        {
          label: "MTD",
          change: operationsData?.orderFillRates?.items?.cMvsGoal ? `${operationsData?.orderFillRates?.items?.cMvsGoal}%` : "0%",
          changely: operationsData?.orderFillRates?.items?.cMvsLM ? `${operationsData?.orderFillRates?.items?.cMvsLM}%` : "0%",
          value: `$${formatToMillions(operationsData?.orderFillRates?.items.currentMonth)}M`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(operationsData?.orderFillRates?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(operationsData?.orderFillRates?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: operationsData?.orderFillRates?.items?.cQvsGoal ? `${operationsData?.orderFillRates?.items?.cQvsGoal}%` : "0%",
          changely: operationsData?.orderFillRates?.items?.cQvsLQ ? `${operationsData?.orderFillRates?.items?.cQvsLQ}%` : "0%",
          value: `$${formatToMillions(operationsData?.orderFillRates?.items?.currentQuarter)}M`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(operationsData?.orderFillRates?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(operationsData?.orderFillRates?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: operationsData?.orderFillRates?.items?.cYvsGoal ? `${operationsData?.orderFillRates?.items?.cYvsGoal}%` : "0%",
          changely: operationsData?.orderFillRates?.items?.cYvsLY ? `${operationsData?.orderFillRates?.items?.cYvsLY}%` : "0%",
          value: `$${formatToMillions(operationsData?.orderFillRates?.items?.currentYear)}M`,
          status: "decrease",
          title: "Year",
          lyValue: `$${formatToMillions(operationsData?.orderFillRates?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(operationsData?.orderFillRates?.items?.goalCurrentYear)}M`
        }
      ]
    },
    {
      title: "Backorder Rate",
      values: [
        {
          label: "MTD",
          change: operationsData?.backOrderRates?.items?.cMvsGoal ? `${operationsData?.backOrderRates?.items?.cMvsGoal}%` : "0%",
          changely: operationsData?.backOrderRates?.items?.cMvsLM ? `${operationsData?.backOrderRates?.items?.cMvsLM}%` : "0%",
          value: `${(((operationsData?.backOrderRates?.items?.currentMonth ?? 0) / 10000) * 100)
            .toFixed(1)
            .replace("0.0", "0")}%`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(operationsData?.backOrderRates?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(operationsData?.backOrderRates?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: operationsData?.backOrderRates?.items?.cQvsGoal ? `${operationsData?.backOrderRates?.items?.cQvsGoal}%` : "0%",
          changely: operationsData?.backOrderRates?.items?.cQvsLQ ? `${operationsData?.backOrderRates?.items?.cQvsLQ}%` : "0%",
          value: `${(((operationsData?.backOrderRates?.items?.currentQuarter ?? 0) / 10000) * 100)
            .toFixed(1)
            .replace("0.0", "0")}%`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(operationsData?.backOrderRates?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(operationsData?.backOrderRates?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: operationsData?.backOrderRates?.items?.cYvsGoal ? `${operationsData?.backOrderRates?.items?.cYvsGoal}%` : "0%",
          changely: operationsData?.backOrderRates?.items?.cYvsLY ? `${operationsData?.backOrderRates?.items?.cYvsLY}%` : "0%",
          value: `${(((operationsData?.backOrderRates?.items?.currentYear ?? 0) / 10000) * 100)
            .toFixed(1)
            .replace("0.0", "0")}%`,
          status: "increase",
          title: "Year",
          lyValue: `$${formatToMillions(operationsData?.backOrderRates?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(operationsData?.backOrderRates?.items?.goalCurrentYear)}M`
        }
      ]
    },
    {
      title: "Forecast Accuracy (MAPE)",
      values: [
        {
          label: "MTD",
          change: operationsData?.forecastAccuracy?.items?.cMvsGoal ? `${operationsData?.forecastAccuracy?.items?.cMvsGoal}%` : "0%",
          changely: operationsData?.forecastAccuracy?.items?.cMvsLM ? `${operationsData?.forecastAccuracy?.items?.cMvsLM}%` : "0%",
          value: `${(((operationsData?.forecastAccuracy?.items?.currentMonth ?? 0) / 10000) * 100)
            .toFixed(1)
            .replace("0.0", "0")}%`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(operationsData?.forecastAccuracy?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(operationsData?.forecastAccuracy?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: operationsData?.forecastAccuracy?.items?.cQvsGoal ? `${operationsData?.forecastAccuracy?.items?.cQvsGoal}%` : "0%",
          changely: operationsData?.forecastAccuracy?.items?.cQvsLQ ? `${operationsData?.forecastAccuracy?.items?.cQvsLQ}%` : "0%",
          value: `${(((operationsData?.forecastAccuracy?.items?.currentQuarter ?? 0) / 10000) * 100)
            .toFixed(1)
            .replace("0.0", "0")}%`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(operationsData?.forecastAccuracy?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(operationsData?.forecastAccuracy?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: operationsData?.forecastAccuracy?.items?.cYvsGoal ? `${operationsData?.forecastAccuracy?.items?.cYvsGoal}%` : "0%",
          changely: operationsData?.forecastAccuracy?.items?.cYvsLY ? `${operationsData?.forecastAccuracy?.items?.cYvsLY}%` : "0%",
          value: `${(((operationsData?.forecastAccuracy?.items?.currentYear ?? 0) / 10000) * 100)
            .toFixed(1)
            .replace("0.0", "0")}%`,
          status: "increase",
          title: "Year",
          lyValue: `$${formatToMillions(operationsData?.forecastAccuracy?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(operationsData?.forecastAccuracy?.items?.goalCurrentYear)}M`
        }
      ]
    },
    {
      title: "Freight as a Percentage of Revenue",
      values: [
        {
          label: "MTD",
          change: operationsData?.freightPercentOfRevenue?.items?.cMvsGoal ? `${operationsData?.freightPercentOfRevenue?.items?.cMvsGoal}%` : "0%",
          changely: operationsData?.freightPercentOfRevenue?.items?.cMvsLM ? `${operationsData?.freightPercentOfRevenue?.items?.cMvsLM}%` : "0%",
          value: `${(((operationsData?.freightPercentOfRevenue?.items?.currentMonth ?? 0) / 10000) * 100)
            .toFixed(1)
            .replace("0.0", "0")}%`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(operationsData?.freightPercentOfRevenue?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(operationsData?.freightPercentOfRevenue?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: operationsData?.freightPercentOfRevenue?.items?.cQvsGoal ? `${operationsData?.freightPercentOfRevenue?.items?.cQvsGoal}%` : "0%",
          changely: operationsData?.freightPercentOfRevenue?.items?.cQvsLQ ? `${operationsData?.freightPercentOfRevenue?.items?.cQvsLQ}%` : "0%",
          value: `${(((operationsData?.freightPercentOfRevenue?.items?.currentQuarter ?? 0) / 10000) * 100)
            .toFixed(1)
            .replace("0.0", "0")}%`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(operationsData?.freightPercentOfRevenue?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(operationsData?.freightPercentOfRevenue?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: operationsData?.freightPercentOfRevenue?.items?.cYvsGoal ? `${operationsData?.freightPercentOfRevenue?.items?.cYvsGoal}%` : "0%",
          changely: operationsData?.freightPercentOfRevenue?.items?.cYvsLY ? `${operationsData?.freightPercentOfRevenue?.items?.cYvsLY}%` : "0%",
          value: `${(((operationsData?.freightPercentOfRevenue?.items?.currentYear ?? 0) / 10000) * 100)
            .toFixed(1)
            .replace("0.0", "0")}%`,
          status: "decrease",
          lyValue: `$${formatToMillions(operationsData?.freightPercentOfRevenue?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(operationsData?.freightPercentOfRevenue?.items?.goalCurrentYear)}M`
        }
      ]
    }
  ];

  //second section timeSeriesCardsdata 
  const timeSeriesCardsdata = [
    {
      title: "Stock Amount (Cases)",
      values: [
        {
          label: lastYearSameMonth,
          change: operationsData?.stockAmountCases?.items?.cmcYvsCMLY ? `${truncateDecimal(operationsData?.stockAmountCases?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${formatToMillions(operationsData?.stockAmountCases?.items?.sameMonthLastYear)}M`,
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${formatToMillions(operationsData?.stockAmountCases?.items?.currentMonth)}M`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: operationsData?.stockAmountCases?.items?.cMvsLM ? `${truncateDecimal(operationsData?.stockAmountCases?.items?.cMvsLM)}%` : "0%",
          value: `$${formatToMillions(operationsData?.stockAmountCases?.items?.lastMonth)}M`,
          status: "increase",
          arrow: "left"
        }
      ]
    },

    {
      title: "Stock Amount (Dollars)",
      values: [
        {
          label: lastYearSameMonth,
          change: operationsData?.stockAmountDollars?.items?.cmcYvsCMLY ? `${truncateDecimal(operationsData?.stockAmountDollars?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${formatToMillions(operationsData?.stockAmountDollars?.items?.sameMonthLastYear)}M`,
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${formatToMillions(operationsData?.stockAmountDollars?.items?.currentMonth)}M`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: operationsData?.stockAmountDollars?.items?.cMvsLM ? `${truncateDecimal(operationsData?.stockAmountDollars?.items?.cMvsLM)}%` : "0%",
          value: `$${formatToMillions(operationsData?.stockAmountDollars?.items?.lastMonth)}M`,
          status: "increase",
          arrow: "left"
        }
      ]
    },

    {
      title: "Inventory Turnover (Units)",
      values: [
        {
          label: lastYearSameMonth,
          change: operationsData?.inventoryTurnOverUnits?.items?.cmcYvsCMLY ? `${truncateDecimal(operationsData?.inventoryTurnOverUnits?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${formatToMillions(operationsData?.inventoryTurnOverUnits?.items?.sameMonthLastYear)}M`,
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${formatToMillions(operationsData?.inventoryTurnOverUnits?.items?.currentMonth)}M`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: operationsData?.inventoryTurnOverUnits?.items?.cMvsLM ? `${truncateDecimal(operationsData?.inventoryTurnOverUnits?.items?.cMvsLM)}%` : "0%",
          value: `$${formatToMillions(operationsData?.inventoryTurnOverUnits?.items?.lastMonth)}M`,
          status: "increase",
          arrow: "left"
        }
      ]
    },

    {
      title: "Perfect Order Rate (OTIF)",
      values: [
        {
          label: lastYearSameMonth,
          change: operationsData?.perfectOrderRates?.items?.cmcYvsCMLY ? `${truncateDecimal(operationsData?.perfectOrderRates?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${formatToMillions(operationsData?.perfectOrderRates?.items?.sameMonthLastYear)}M`,
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${formatToMillions(operationsData?.perfectOrderRates?.items?.currentMonth)}M`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: operationsData?.perfectOrderRates?.items?.cMvsLM ? `${truncateDecimal(operationsData?.perfectOrderRates?.items?.cMvsLM)}%` : "0%",
          value: `$${formatToMillions(operationsData?.perfectOrderRates?.items?.lastMonth)}M`,
          status: "increase",
          arrow: "left"
        }
      ]
    },

    {
      title: "On Time Delivery (OT)",
      values: [
        {
          label: lastYearSameMonth,
          change: operationsData?.onTimeDeliveryRates?.items?.cmcYvsCMLY ? `${truncateDecimal(operationsData?.onTimeDeliveryRates?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${formatToMillions(operationsData?.onTimeDeliveryRates?.items?.sameMonthLastYear)}M`,
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${formatToMillions(operationsData?.onTimeDeliveryRates?.items?.currentMonth)}M`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: operationsData?.onTimeDeliveryRates?.items?.cMvsLM ? `${truncateDecimal(operationsData?.onTimeDeliveryRates?.items?.cMvsLM)}%` : "0%",
          value: `$${formatToMillions(operationsData?.onTimeDeliveryRates?.items?.lastMonth)}M`,
          status: "increase",
          arrow: "left"
        }
      ]
    },

    {
      title: "Order Fill Rate (IF)",
      values: [
        {
          label: lastYearSameMonth,
          change: operationsData?.orderFillRates?.items?.cmcYvsCMLY ? `${truncateDecimal(operationsData?.orderFillRates?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${formatToMillions(operationsData?.orderFillRates?.items?.sameMonthLastYear)}M`,
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${formatToMillions(operationsData?.orderFillRates?.items?.currentMonth)}M`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: operationsData?.orderFillRates?.items?.cMvsLM ? `${truncateDecimal(operationsData?.orderFillRates?.items?.cMvsLM)}%` : "0%",
          value: `$${formatToMillions(operationsData?.orderFillRates?.items?.lastMonth)}M`,
          status: "increase",
          arrow: "left"
        }
      ]
    },

    {
      title: "Backorder Rate",
      values: [
        {
          label: lastYearSameMonth,
          change: operationsData?.backOrderRates?.items?.cmcYvsCMLY ? `${truncateDecimal(operationsData?.backOrderRates?.items?.cmcYvsCMLY)}%` : "0%",
          value: `${(
            (operationsData?.backOrderRates?.items?.sameMonthLastYear ?? 0) / 100
          ).toFixed(1)}%`,
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `${(
            (operationsData?.backOrderRates?.items?.currentMonth ?? 0) / 100
          ).toFixed(1)}%`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: operationsData?.backOrderRates?.items?.cMvsLM ? `${truncateDecimal(operationsData?.backOrderRates?.items?.cMvsLM)}%` : "0%",
          value: `${(
            (operationsData?.backOrderRates?.items?.lastMonth ?? 0) / 100
          ).toFixed(1)}%`,
          status: "increase",
          arrow: "left"
        }
      ]
    },

    {
      title: "Forecast Accuracy (MAPE)",
      values: [
        {
          label: lastYearSameMonth,
          change: operationsData?.forecastAccuracy?.items?.cmcYvsCMLY ? `${truncateDecimal(operationsData?.forecastAccuracy?.items?.cmcYvsCMLY)}%` : "0%",
          value: `${(
            (operationsData?.forecastAccuracy?.items?.sameMonthLastYear ?? 0) / 100
          ).toFixed(1)}%`,
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `${(
            (operationsData?.forecastAccuracy?.items?.currentMonth ?? 0) / 100
          ).toFixed(1)}%`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: operationsData?.forecastAccuracy?.items?.cMvsLM ? `${truncateDecimal(operationsData?.forecastAccuracy?.items?.cMvsLM)}%` : "0%",
          value: `${(
            (operationsData?.forecastAccuracy?.items?.lastMonth ?? 0) / 100
          ).toFixed(1)}%`,
          status: "increase",
          arrow: "left"
        }
      ]
    },

    {
      title: "Freight as a Percentage of Revenue",
      values: [
        {
          label: lastYearSameMonth,
          change: operationsData?.freightPercentOfRevenue?.items?.cmcYvsCMLY ? `${truncateDecimal(operationsData?.freightPercentOfRevenue?.items?.cmcYvsCMLY)}%` : "0%",
          value: `${(
            (operationsData?.freightPercentOfRevenue?.items?.sameMonthLastYear ?? 0) / 100
          ).toFixed(1)}%`,
          status: "increase", arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `${(
            (operationsData?.freightPercentOfRevenue?.items?.currentMonth ?? 0) / 100
          ).toFixed(1)}%`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: operationsData?.freightPercentOfRevenue?.items?.cMvsLM ? `${truncateDecimal(operationsData?.freightPercentOfRevenue?.items?.cMvsLM)}%` : "0%",
          value: `${(
            (operationsData?.freightPercentOfRevenue?.items?.lastMonth ?? 0) / 100
          ).toFixed(1)}%`,
          status: "decrease",
          arrow: "left"
        }
      ]
    }
  ];

  // third section cards 
  const highlightCards = [
    // --- Backordered SKU ---
    {
      title: "#1 Backordered SKU",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(operationsData?.backorderedSKUs?.items[0]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(operationsData?.backorderedSKUs?.items[0]?.netSales)}M`,
      bigTitle: operationsData?.backorderedSKUs?.items[0]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(operationsData?.backorderedSKUs?.items[0]?.lastPeriodNetSales)}M`
    },
    {
      title: "#2 Backordered SKU",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(operationsData?.backorderedSKUs?.items[1]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(operationsData?.backorderedSKUs?.items[1]?.netSales)}M`,
      bigTitle: operationsData?.backorderedSKUs?.items[1]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(operationsData?.backorderedSKUs?.items[1]?.lastPeriodNetSales)}M`
    },
    {
      title: "#3 Backordered SKU",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(operationsData?.backorderedSKUs?.items[2]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(operationsData?.backorderedSKUs?.items[2]?.netSales)}M`,
      bigTitle: operationsData?.backorderedSKUs?.items[2]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(operationsData?.backorderedSKUs?.items[2]?.lastPeriodNetSales)}M`
    },

    // --- Lowest OTIF Customers ---
    {
      title: "#1 Lowest OTIF Customer",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(operationsData?.lowestOTIFCustomers?.items[0]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(operationsData?.lowestOTIFCustomers?.items[0]?.netSales)}M`,
      bigTitle: operationsData?.lowestOTIFCustomers?.items[0]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(operationsData?.lowestOTIFCustomers?.items[0]?.lastPeriodNetSales)}M`
    },
    {
      title: "#2 Lowest OTIF Customer",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(operationsData?.lowestOTIFCustomers?.items[1]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(operationsData?.lowestOTIFCustomers?.items[1]?.netSales)}M`,
      bigTitle: operationsData?.lowestOTIFCustomers?.items[1]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(operationsData?.lowestOTIFCustomers?.items[1]?.lastPeriodNetSales)}M`
    },
    {
      title: "#3 Lowest OTIF Customer",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(operationsData?.lowestOTIFCustomers?.items[2]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(operationsData?.lowestOTIFCustomers?.items[2]?.netSales)}M`,
      bigTitle: operationsData?.lowestOTIFCustomers?.items[2]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(operationsData?.lowestOTIFCustomers?.items[2]?.lastPeriodNetSales)}M`
    },

    // --- Lowest OTIF Fulfillment Warehouse ---
    {
      title: "#1 Lowest OTIF Fulfillment Warehouse",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(operationsData?.lowestOTIFFulfillmentWarehouses?.items[0]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(operationsData?.lowestOTIFFulfillmentWarehouses?.items[0]?.netSales)}M`,
      bigTitle: operationsData?.lowestOTIFFulfillmentWarehouses?.items[0]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(operationsData?.lowestOTIFFulfillmentWarehouses?.items[0]?.lastPeriodNetSales)}M`
    },
    {
      title: "#2 Lowest OTIF Fulfillment Warehouse",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(operationsData?.lowestOTIFFulfillmentWarehouses?.items[1]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(operationsData?.lowestOTIFFulfillmentWarehouses?.items[1]?.netSales)}M`,
      bigTitle: operationsData?.lowestOTIFFulfillmentWarehouses?.items[1]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(operationsData?.lowestOTIFFulfillmentWarehouses?.items[1]?.lastPeriodNetSales)}M`
    },
    {
      title: "#3 Lowest OTIF Fulfillment Warehouse",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(operationsData?.lowestOTIFFulfillmentWarehouses?.items[2]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(operationsData?.lowestOTIFFulfillmentWarehouses?.items[2]?.netSales)}M`,
      bigTitle: operationsData?.lowestOTIFFulfillmentWarehouses?.items[2]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(operationsData?.lowestOTIFFulfillmentWarehouses?.items[2]?.lastPeriodNetSales)}M`
    }
  ];


  const [openFilters, setOpenFilters] = useState(false);
  const handleToggleFilters = () => {
    setOpenFilters((prev) => !prev);
  };

  // Filter state
  const [activeFilter, setActiveFilter] = useState('All');
  const [filterLoading, setFilterLoading] = useState(false);

  // Define filter presets for each quick filter
  const getFilterPreset = useCallback((filterName) => {
    if (!filterOptions) return null;

    // Helper to extract names from filter options
    const getNames = (key) => (filterOptions[key] || []).map(v => v?.name || v);

    // Get all available options dynamically
    const allCompanies = getNames("Company");
    const allCategories = getNames("Category");
    const allBrands = getNames("Brand");
    const allSubBrands = getNames("Sub-Brand");
    const allRoutes = getNames("Route to Market");
    const allChannels = getNames("Channel");

    // Helper to get Nutrabolt brands/sub-brands (exclude Bloom)
    const getNutraboltBrands = () => {
      return allBrands
        .filter(brand => {
          const brandName = typeof brand === 'string' ? brand : brand?.name;
          return brandName && !brandName.toLowerCase().includes('bloom');
        })
        .map(b => typeof b === 'string' ? b : b?.name);
    };

    const getNutraboltSubBrands = () => {
      return allSubBrands
        .filter(subBrand => {
          const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
          return subBrandName && !subBrandName.toLowerCase().includes('bloom');
        })
        .map(sb => typeof sb === 'string' ? sb : sb?.name);
    };

    // Helper to get Bloom brands/sub-brands
    const getBloomBrands = () => {
      return allBrands
        .filter(brand => {
          const brandName = typeof brand === 'string' ? brand : brand?.name;
          return brandName && brandName.toLowerCase().includes('bloom');
        })
        .map(b => typeof b === 'string' ? b : b?.name);
    };

    const getBloomSubBrands = () => {
      return allSubBrands
        .filter(subBrand => {
          const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
          return subBrandName && subBrandName.toLowerCase().includes('bloom');
        })
        .map(sb => typeof sb === 'string' ? sb : sb?.name);
    };

    // Helper to get Functional Beverage sub-brands (beverage products, exclude powders)
    const getFunctionalBeverageSubBrands = () => {
      return allSubBrands
        .filter(subBrand => {
          const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
          // Exclude powder/supplement sub-brands
          return subBrandName &&
            !subBrandName.toLowerCase().includes('powder') &&
            !subBrandName.toLowerCase().includes('cellucor') &&
            subBrandName !== 'XTEND' &&
            subBrandName !== 'C4';
        })
        .map(sb => typeof sb === 'string' ? sb : sb?.name);
    };

    // Helper to get Active Nutrition sub-brands (supplements/powders)
    const getActiveNutritionSubBrands = () => {
      return allSubBrands
        .filter(subBrand => {
          const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
          // Include powder and supplement sub-brands
          return subBrandName && (
            subBrandName.toLowerCase().includes('powder') ||
            subBrandName === 'XTEND' ||
            subBrandName === 'C4' ||
            subBrandName.toLowerCase().includes('cellucor')
          );
        })
        .map(sb => typeof sb === 'string' ? sb : sb?.name);
    };

    // Helper to find category by name
    const findCategory = (searchTerm) => {
      return allCategories.find(cat => {
        const catName = typeof cat === 'string' ? cat : cat?.name;
        return catName && catName.toLowerCase().includes(searchTerm.toLowerCase());
      });
    };

    switch (filterName) {
      case 'All':
        return {
          Company: allCompanies,
          Category: allCategories,
          Brand: allBrands,
          "Sub-Brand": allSubBrands,
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Nutrabolt':
        return {
          Company: ["Nutrabolt"],
          Category: allCategories,
          Brand: getNutraboltBrands(),
          "Sub-Brand": getNutraboltSubBrands(),
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Bloom':
        const bloomCategory = findCategory('functional');
        return {
          Company: ["Bloom"],
          Category: bloomCategory ? [typeof bloomCategory === 'string' ? bloomCategory : bloomCategory?.name] : [],
          Brand: getBloomBrands(),
          "Sub-Brand": getBloomSubBrands(),
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Functional Beverage':
        const functionalBeverageCategory = findCategory('functional');
        const functionalBeverageBrands = allBrands
          .filter(brand => {
            const brandName = typeof brand === 'string' ? brand : brand?.name;
            return brandName && (brandName === 'C4' || brandName.toLowerCase().includes('bloom'));
          })
          .map(b => typeof b === 'string' ? b : b?.name);
        return {
          Company: allCompanies,
          Category: functionalBeverageCategory ? [typeof functionalBeverageCategory === 'string' ? functionalBeverageCategory : functionalBeverageCategory?.name] : [],
          Brand: functionalBeverageBrands,
          "Sub-Brand": getFunctionalBeverageSubBrands(),
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Active Nutrition':
        const activeNutritionCategory = findCategory('active');
        return {
          Company: ["Nutrabolt"],
          Category: activeNutritionCategory ? [typeof activeNutritionCategory === 'string' ? activeNutritionCategory : activeNutritionCategory?.name] : [],
          Brand: getNutraboltBrands(),
          "Sub-Brand": getActiveNutritionSubBrands(),
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      default:
        return null;
    }
  }, [filterOptions]);
  const [filterPresets, setFilterPresets] = useState(() => getFilterPreset('All'));
  useEffect(() => {
    if (filterOptions && !filterPresets) {
      const allPreset = getFilterPreset('All');
      if (allPreset) {
        setFilterPresets(allPreset);
      }
    }
  }, [filterOptions, filterPresets, getFilterPreset]);


  // Handle filter click with full-page loader
  const handleFilterClick = (filter) => {
    if (filter === activeFilter || filterLoading) return;

    setFilterLoading(true);
    setLoading(true); // Show full-page loader

    // Simulate loading delay
    setTimeout(() => {
      const preset = getFilterPreset(filter);
      setActiveFilter(filter);

      // Set the preset - this will trigger FilterPanelContainer to apply it
      if (preset) {
        setFilterPresets(preset);
        if (setActiveFilters) {
          setActiveFilters(preset);
        }
      } else {
        setFilterPresets(null);
      }

      setFilterLoading(false);
      setLoading(false); // Hide full-page loader

      // Open filter panel for "Custom" filter
      if (filter === 'Custom') {
        setOpenFilters(true);
      }
    }, 800);
  };

  // Handle filter changes from FilterPanelContainer
  const handleFiltersChange = useCallback((newFilters) => {
    // Don't check for preset match if we're already in Custom mode
    // FilterPanelContainer's isApplyingPresetRef prevents callback when applying presets
    if (activeFilter === 'Custom') return;

    // Check if the current filters match any preset
    const presetNames = ['All', 'Nutrabolt', 'Bloom', 'Functional Beverage', 'Active Nutrition'];
    let matchedPreset = null;

    for (const presetName of presetNames) {
      const preset = getFilterPreset(presetName);
      if (preset) {
        const isMatching = Object.keys(preset).every(key => {
          const presetValues = [...(preset[key] || [])].sort();
          const currentValues = [...(newFilters[key] || [])].sort();
          return presetValues.length === currentValues.length &&
            presetValues.every((val, idx) => val === currentValues[idx]);
        });

        if (isMatching) {
          matchedPreset = presetName;
          break;
        }
      }
    }

    // If filters don't match any preset, switch to Custom
    if (!matchedPreset && activeFilter !== 'Custom') {
      setActiveFilter('Custom');
      setFilterPresets(null);
    }
  }, [activeFilter, getFilterPreset]);

  // Handler for Highlights section chips
  const handleHighlightsChipClick = (idx, label) => {
    setHighlightsActiveIdx(idx);
    setHighlightsActiveChipName(label);
    console.log("Clicked Highlights Chip Name:", label);
  };

  // Handler for Visualizations section chips
  const handleVisualizationChipClick = (idx, label) => {
    setVisualizationActiveIdx(idx);
    setVisualizationActiveChipName(label);
    console.log("Clicked Visualization Chip Name:", label);
  };






  const getSubBrandColor = (subBrandName) => {
    if (!subBrandName) return "#000000";

    const normalized = subBrandName.trim();

    const colorMap = {
      "C4 Powder": "#C7D244",
      "Bloom Pop": "#6CA655",
      "Bloom Sparkling Energy": "#BD93E3",
      "XTEND": "#D0D0D0",
      "Cellucor": "#A6A6A6",
      "C4 Ultimate": "#000000",
      "C4 Smart Energy": "#3FB4E5",
      "C4 Performance": "#E8D22F",
    };

    return colorMap[normalized] || "#BDBDBD"; // fallback
  };
  const pieDataSubBrands = useMemo(() => {
    const items = operationsData?.stockAmountBySubBrand?.items;

    if (!Array.isArray(items) || items.length === 0) return [];

    return items.map((item) => ({
      label: item.name || "",
      value: Number(item.percentage?.toFixed(1)) || 0, // or item.stockAmount
      color: getSubBrandColor(item.name),
    }));
  }, [operationsData]);



  const getRegionColor = (regionName) => {
    if (!regionName) return "#000000";

    const normalized = regionName.trim();

    const colorMap = {
      "West": "#4BB056",
      "North": "#BFD3EA",
      "South": "#9962CC",
      "South Central": "#F0C287",
      "Northeast": "#E15242",
      "Central": "#DBD88B",
    };

    return colorMap[normalized] || "#BDBDBD"; // fallback color
  };
  const pieDataRegions = useMemo(() => {
    const items = operationsData?.stockAmountByRegion?.items;

    if (!Array.isArray(items) || items.length === 0) return [];

    return items.map((item) => ({
      label: item?.name || "",
      value: Number(item?.percentage?.toFixed(1)) || 0, // or item.value
      color: getRegionColor(item?.name),
    }));
  }, [operationsData]);

  const getWarehouseColor = (warehouseName) => {
    if (!warehouseName) return "#000000";

    const normalized = warehouseName.trim();

    const colorMap = {
      SERD: "#5068AE",
      REDP: "#A4BF52",
      SHPL: "#9B3B68",
      SFH: "#0A6B71",
      NELF: "#DE7C29",
    };

    return colorMap[normalized] || "#000000";
  };

  const pieDataWarehouses = useMemo(() => {
    const items =
      operationsData?.otifFailsByWarehouses?.items;

    if (!Array.isArray(items) || items.length === 0) return [];

    return items.map((item) => ({
      label: item.warehouseName,
      value: Number(item.percentage?.toFixed(1)) || 0,        // or item.percentage
      color: getWarehouseColor(item.warehouseName),
    }));
  }, [operationsData]);

  console.log(pieDataWarehouses, "pieDataWarehouses")



  const getCustomerColor = (customerName) => {
    if (!customerName) return "#000000";

    const normalized = customerName?.trim();

    const colorMap = {
      "Other": "#D8A62F",
      "Watkins Distributing": "#0A8F6B",
      "Great Basin Beverage": "#F190A3",
      "BJ’s": "#D63D6C",
      "7-Eleven": "#000000",
      "Sam’s Club": "#1E446C",
    };

    return colorMap[normalized] || "#000000";
  };
  const pieDataCustomer = useMemo(() => {
    const items =



      operationsData?.otifFailsCustomers?.items;

    if (!Array.isArray(items) || items.length === 0) return [];

    return items
      .slice(0, 5) //first five only
      .map((item, index) => ({
        label: item?.customerName,
        value: Number(item.percentage?.toFixed(1)) || 0,
        color: getCustomerColor(item?.customerName),
      }));
  }, [operationsData]);


  const getCasesChartColor = (key) => {
    const colorMap = {
      stock: "#A44EE6",
      sold: "#4243AC",
      forecast: "#AA2759",
    };
    return colorMap[key] || "#000000";
  };
  const convertCasesInStockVsOrderedChart = useMemo(() => {
    const apiItems =
      operationsData?.casesInStockVsOrderedMonthlies?.items || [];

    if (!Array.isArray(apiItems) || apiItems.length === 0) return null;

    // Sort by year + monthNumber (important)
    const sortedItems = [...apiItems].sort(
      (a, b) => a.year - b.year || a.monthNumber - b.monthNumber
    );

    // xLabels (Jan. 2025 format)
    const xLabels = sortedItems.map((item) =>
      item.month.replace("-", ". ")
    );

    // Series (dynamic, same order as sample)
    const selectedKeys = ["stock", "sold", "forecast"];

    const series = selectedKeys.map((key) => ({
      key,
      label:
        key === "stock"
          ? "Cases In Stock"
          : key === "sold"
            ? "Cases Sold"
            : "Demand Forecast",
      dashed: key === "forecast",
      color: getCasesChartColor(key),
    }));

    // DataMatrix → ORDER: stock, sold, forecast
    const dataMatrix = sortedItems.map((item) => [
      +(item.stockCurrentMonth / 1000).toFixed(1),
      +(item.casesSoldCurrentMonth / 1000).toFixed(1),
      +(item.forecastCurrentMonth / 1000).toFixed(1),
    ]);
    // Flatten all values in dataMatrix to a single array
    const allValues = dataMatrix.flat();

    // Get the max value
    const maxValue = Math.max(...allValues, 0);

    // Function to generate nice ticks dynamically
    function generateNiceTicks(maxVal, tickCount = 5) {
      if (maxVal === 0) return [0];

      const roughStep = maxVal / tickCount;
      const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
      const normalized = roughStep / magnitude;

      let niceNormalized;

      if (normalized <= 1) niceNormalized = 1;
      else if (normalized <= 2) niceNormalized = 2;
      else if (normalized <= 5) niceNormalized = 5;
      else niceNormalized = 10;

      const step = niceNormalized * magnitude;
      const maxTick = Math.ceil(maxVal / step) * step;

      const ticks = [];
      for (let i = 0; i <= maxTick + step / 2; i += step) {
        ticks.push(Number(i.toFixed(6)));
      }

      return ticks;
    }

    const yTicks = generateNiceTicks(maxValue, 5);
    return {
      title: "Cases In Stock vs Ordered Over Time",
      xLabels,
      series,
      dataMatrix,
      yTicks
    };
  }, [operationsData]);
  console.log(convertCasesInStockVsOrderedChart, "convertCasesInStockVsOrderedChart")


  const getWarehouseColors = (warehouse) => {
    const colorMap = {
      SFH: "#0B7D70",   // teal
      NELF: "#E27826",  // orange
      SERD: "#5C7AEA",  // blue
      REDP: "#7C9A27",  // green
      SHPL: "#B12F7A"   // magenta
    };

    return colorMap[warehouse] || "#999999"; // fallback color
  };

  const convertOTIFFailsWarehouseDynamic = useMemo(() => {
    const apiItems =
      operationsData?.otifFailsByWarehouseMonthlies?.items || [];

    if (!Array.isArray(apiItems) || apiItems.length === 0) return null;

    //  Sort months chronologically
    const sortedItems = [...apiItems].sort(
      (a, b) => new Date("01-" + a.month) - new Date("01-" + b.month)
    );

    // X Labels (match your format)
    const xLabels = sortedItems.map(item =>
      item.month.replace("-", ". ")
    );

    // Collect ALL warehouse names dynamically
    const warehouseSet = new Set();

    sortedItems.forEach(monthItem => {
      monthItem.values.forEach(v => {
        warehouseSet.add(v.warehouseName);
      });
    });

    const labels = Array.from(warehouseSet);

    // Series (color mapped dynamically)
    const series = labels.map(label => ({
      label,
      color: getWarehouseColors(label) // ← your color function
    }));

    // Build Data Matrix
    const dataMatrix = sortedItems.map(monthItem => {
      const valueMap = {};

      monthItem.values.forEach(v => {
        valueMap[v.warehouseName] = v.failCaseCount;
      });

      // Fill missing warehouses with 0
      return labels.map(label => valueMap[label] || 0);
    });

    //  Final structure
    return {
      title: "OTIF Fails by Warehouse",
      xLabels,
      series,
      dataMatrix
    };

  }, [operationsData]);
  console.log(convertOTIFFailsWarehouseDynamic, "convertOTIFFailsWarehouseDynamic")

  const getSubBrandchartColor = (subBrandName) => {
    const colorMap = {
      "C4 Powder": "#C2CF2D",
      "C4 Performance": "#ECD01C",
      "C4 Smart Energy": "#1BA4E6",
      "C4 Ultimate": "#1A1A1A",
      "Cellucor": "#747474",
      "XTEND": "#C5C5C5",
      "Bloom Sparkling Energy": "#C393E4",
      "Bloom Pop": "#59A671",
      "C4": "#C2CF2D",
      "Bloom": "#59A671"
    };

    return colorMap[subBrandName] || "#000000";
  };
  const convertCasesForecastLineChart = useMemo(() => {
    const apiItems =
      operationsData?.casesOrderedVsForecastedSubBrandMonthlies?.items || [];

    if (!Array.isArray(apiItems) || apiItems.length === 0) return null;

    // Sort Months Chronologically
    const sortedItems = [...apiItems].sort(
      (a, b) => new Date("01-" + a.month) - new Date("01-" + b.month)
    );

    //xLabels Format: "Apr. 2023"
    const xLabels = sortedItems.map((item) =>
      item.month.replace("-", ". ")
    );

    // -------------------------------------------------------
    //Step 1: Collect Brand Names Dynamically
    // -------------------------------------------------------
    const brandSet = new Set();

    sortedItems.forEach((monthItem) => {
      monthItem.values.forEach((v) => {
        let brand = v.subBrandName;

        // Normalize API names
        if (brand === "C4") brand = "C4 Powder";
        if (brand === "Bloom") brand = "Bloom Pop";

        brandSet.add(brand);
      });
    });

    // Convert Set → Array
    const labels = Array.from(brandSet);

    // -------------------------------------------------------
    const series = labels.map((label) => ({
      label,
      color: getSubBrandchartColor(label)
    }));

    // Step 3: Build Actual + Forecast Matrices Dynamically
    const actualMatrix = [];
    const forecastMatrix = [];

    sortedItems.forEach((monthItem) => {
      const valueMap = {};

      // Store actual + forecast in lookup map
      monthItem.values.forEach((v) => {
        let brand = v.subBrandName;

        if (brand === "C4") brand = "C4 Powder";
        if (brand === "Bloom") brand = "Bloom Pop";

        valueMap[brand] = {
          actual: v.actualCases / 1_000_000,
          forecast: v.forecastCases / 1_000_000
        };
      });

      // Row must follow dynamic labels order
      const actualRow = labels.map(
        (label) => +(valueMap[label]?.actual || 0).toFixed(2)
      );

      const forecastRow = labels.map(
        (label) => +(valueMap[label]?.forecast || 0).toFixed(2)
      );

      actualMatrix.push(actualRow);
      forecastMatrix.push(forecastRow);
    });
    const dataMatrix = {
      actual: actualMatrix,
      forecast: forecastMatrix
    }
    const allValues = [
      ...dataMatrix?.actual?.flat(),
      ...dataMatrix?.forecast?.flat()
    ];

    const maxValue = Math.max(...allValues);

    // Avoid zero issue
    let yTicks = [0];

    if (maxValue > 0) {
      const rawStep = maxValue / 5;

      const magnitude = Math.pow(
        10,
        Math.floor(Math.log10(rawStep))
      );

      const niceStep = Math.ceil(rawStep / magnitude) * magnitude;

      yTicks = Array.from({ length: 6 }, (_, i) =>
        Number((i * niceStep).toFixed(2))
      );
    }
    // -------------------------------------------------------
    return {
      title: "Cases Ordered vs Forecasted by Sub-Brand",
      xLabels,
      series,
      dataMatrix,
      yTicks
    };
  }, [operationsData]);
  console.log("convertCasesForecastLineChart:", convertCasesForecastLineChart);

  const getBackorderedSeriesColor = (key) => {
    const colorMap = {
      backordered: "#000000",
      stock: "#A24CE1"
    };
    return colorMap[key] || "#000000";
  };

  const convertTopSkuBackorderChartData = useMemo(() => {
    const apiItems = operationsData?.top10BackorderedSKUs?.items || [];

    if (!Array.isArray(apiItems) || apiItems.length === 0) return null;

    // Sort by rank if available, otherwise by value descending
    const sortedItems = [...apiItems].sort((a, b) => (a.rank || 0) - (b.rank || 0));

    const xLabels = sortedItems.map(item => item.skuName);

    // Dynamic series keys
    const seriesKeys = ["backordered"];

    // Map series dynamically using the helper function
    const series = seriesKeys.map(key => ({
      label: key === "backordered" ? "Backordered Cases" : "Cases In Stock",
      key,
      color: getBackorderedSeriesColor(key)
    }));

    // Construct data matrix dynamically
    const dataMatrix = {
      actual: sortedItems.map(item => seriesKeys.map(key => {
        // Map value to the correct series index
        if (key === "backordered") return item.value;
        return 0;
      }))
    };

    // Calculate dynamic xTicks
    const maxValue = Math.max(...sortedItems.map(item => item.value), 0);
    const step = 500;
    const maxTick = Math.ceil((maxValue + 100) / step) * step;
    const xTicks = [];
    for (let i = 0; i <= maxTick; i += step) {
      xTicks.push(i);
    }

    return {
      title: "Top 10 Backordered SKUs",
      xLabels,
      series,
      dataMatrix,
      xTicks
    };
  }, [operationsData]);

  console.log(convertTopSkuBackorderChartData, "convertTopSkuBackorderChartData")

  const getStockedSeriesColor = (key) => {
    const colorMap = {
      inStock: "#A24CE1",
      sold: "#243A74"
    };
    return colorMap[key] || "#000000";
  };

  const convertTopStockedSkuChartData = useMemo(() => {
    const apiItems = operationsData?.top10StockedSKUs?.items || [];

    if (!Array.isArray(apiItems) || apiItems.length === 0) return null;

    // Sort by rank if available, otherwise by value descending
    const sortedItems = [...apiItems].sort((a, b) => (a.rank || 0) - (b.rank || 0));

    const xLabels = sortedItems.map(item => item.skuName);

    // Dynamic series keys - currently API only provides one value which we assume is 'inStock'
    const seriesKeys = ["inStock"];

    const series = seriesKeys.map(key => ({
      label: key === "inStock" ? "Cases In Stock" : "Cases Sold",
      key,
      color: getStockedSeriesColor(key)
    }));

    const dataMatrix = {
      actual: sortedItems.map(item => seriesKeys.map(key => {
        if (key === "inStock") return item.value;
        return 0;
      }))
    };

    // Calculate dynamic xTicks
    const maxValue = Math.max(...sortedItems.map(item => item.value), 0);
    const step = 1000;
    const maxTick = Math.ceil((maxValue + 100) / step) * step;
    const xTicks = [];
    for (let i = 0; i <= maxTick; i += step) {
      xTicks.push(i);
    }

    return {
      title: "Top 10 Stocked SKUs",
      xLabels,
      series,
      dataMatrix,
      xTicks
    };
  }, [operationsData]);

  console.log(convertTopStockedSkuChartData, "convertTopStockedSkuChartData");

  const getOverTimeSeriesColor = (key) => {
    const colorMap = {
      perfectOrderRate: "#0B5ED7",
      onTimeDelivery: "#00A86B",
      orderFillRate: "#FF8C42"
    };
    return colorMap[key] || "#000000";
  };

  const convertOverTimeChartData = useMemo(() => {
    const apiItems = operationsData?.otifOverTimes?.items || [];

    if (!Array.isArray(apiItems) || apiItems.length === 0) return null;

    // Helper to parse "MMM-yyyy" for sorting
    const parseMonth = (str) => {
      const parts = str.split('-');
      if (parts.length !== 2) return new Date();
      return new Date(`${parts[0]} 1, ${parts[1]}`);
    };

    const sortedItems = [...apiItems].sort((a, b) => parseMonth(a.month) - parseMonth(b.month));

    const xLabels = sortedItems.map(item => {
      const parts = item.month.split('-');
      if (parts.length === 2) {
        return `${parts[0]}. ${parts[1]}`;
      }
      return item.month;
    });

    const seriesKeys = ["perfectOrderRate", "onTimeDelivery", "orderFillRate"];

    const series = seriesKeys.map(key => ({
      label: key === "perfectOrderRate" ? "Perfect Order Rate (OTIF)" :
        key === "onTimeDelivery" ? "On Time Delivery (OT)" :
          "Order Fill Rate (IF)",
      key,
      color: getOverTimeSeriesColor(key)
    }));

    const dataMatrix = sortedItems.map(item => seriesKeys.map(key => Number(item[key] || 0)));

    // Calculate dynamic yTicks
    const allValues = dataMatrix.flat();
    const maxValue = Math.max(...allValues, 0);

    // Determine step size based on max value
    let step = 20;
    if (maxValue > 200) step = 100;
    else if (maxValue > 100) step = 25;

    const maxTick = Math.ceil((maxValue + (step / 2)) / step) * step;

    const yTicks = [];
    for (let i = 0; i <= maxTick; i += step) {
      yTicks.push(i);
    }

    return {
      title: "OTIF, OT, and IF Over Time",
      xLabels,
      series,
      dataMatrix,
      yTicks
    };
  }, [operationsData]);
  console.log(convertOverTimeChartData, "convertOverTimeChartData")

  const [hover, setHover] = React.useState(false);
  return (


    <Box sx={{
      ml: 0,
      transition: "margin-left 0.25s ease",
      "& .MuiBox-root": { backgroundImage: "none" }
    }} style={{
      padding: isMobile ? '8px' : '0px 16px 0px 16px',
      fontFamily: 'inter !important'
    }} className="top-bar-custom">
      {/* Loading Backdrop */}
      <Backdrop
        open={loading}
        sx={{
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
        }}
      >
        <CircularProgress color="primary" />
      </Backdrop>

      <Box
        sx={{
          height: {
            xs: "calc(100dvh - 140px)", // mobile header
            md: "calc(100vh - 64px)",  // desktop header
          },
          overflowY: "auto",
          // px: isMobile ? 1 : 2,
          pb: "100px",
          /* smooth scrolling */
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch", // iOS momentum

          /* hide scrollbar */
          scrollbarWidth: "none",          // Firefox
          msOverflowStyle: "none",         // IE / Edge
          "&::-webkit-scrollbar": {
            display: "none",               // Chrome / Safari
          },
        }}
      >
        {/* Section Title: KPI's */}
        <Typography sx={{ fontSize: '16px', mb: 1, mt: 3 }}>
          <span style={{ color: '#1672B980', fontWeight: 200, fontSize: "16px" }}>Operations / </span><span style={{ fontWeight: 200, fontSize: "16px" }}>KPI's</span>
        </Typography>
        <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        <KpiMetricscards cardsData={Kpiscardsdata} />

        {/* Section Title: Time Series */}
        <Typography sx={{ fontSize: '16px', mb: 1, mt: 3 }}>
          <span style={{ color: '#1672B980', fontWeight: 200, fontSize: "16px" }}>Operations / </span><span style={{ fontWeight: 200, fontSize: "16px" }}>Time Series</span>
        </Typography>
        <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        <TimeSeriescards timeSeriesCards={timeSeriesCardsdata} />

        {/* Section Title: Highlights with YTD/QTD/MTD buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 3 }}>
          <Typography sx={{ fontSize: '16px' }}>
            <span style={{ color: '#1672B980', fontWeight: 200, fontSize: "16px" }}>Operations / </span><span style={{ fontWeight: 200, fontSize: "16px" }}>Highlights</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {['YTD', 'QTD', 'MTD'].map((label, idx) => (
              <FilterChip
                key={label}
                label={label}
                idx={idx}
                activeIdx={highlightsActiveIdx}
                onClick={handleHighlightsChipClick}
              />
            ))}
          </Box>
        </Box>
        <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        <HighlightsCards data={highlightCards} />

        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontSize: '16px' }}>
              <span style={{ color: "#1672B980", fontWeight: 200, fontSize: "16px" }}>Operations / </span> <span style={{ fontSize: "16px", fontWeight: 200 }}>Visualizations</span>
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {["YTD", "QTD", "MTD"].map((label, idx) => (
                <FilterChip
                  key={label}
                  label={label}
                  idx={idx}
                  activeIdx={visualizationActiveIdx}
                  onClick={handleVisualizationChipClick}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        </Box>
        <Grid
          container
          rowSpacing={2}
          columnSpacing={2}
          sx={{
            mt: "26px !important",
            alignItems: "stretch"   // keeps all cards same height
          }}
        >
          <Grid item xs={12} sm={6} md={3} sx={{
            pt: { xs: "0px !important", sm: "0px !important", md: "0px !important" }
          }} >
            <PieLegendCard title="Stock Amount by Sub Brand" data={pieDataSubBrands} />
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{
            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
          }}>
            <PieLegendCard title="Stock Amount by Region" data={pieDataRegions} />
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{
            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
          }} >
            <PieLegendCard title="OTIF Fails by Warehouse" data={pieDataWarehouses} />
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{
            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
          }}>
            <PieLegendCard title="OTIF Fails by Top 5 Customers" data={pieDataCustomer} />
          </Grid>
        </Grid>
        <Grid container sx={{ mt: "34px !important" }}>
          <Grid item xs={12}>
            {convertOverTimeChartData?.xLabels?.length > 0 &&
              convertOverTimeChartData?.series?.length > 0 &&
              convertOverTimeChartData?.dataMatrix?.length > 0 && (
                <MultiBarcharts
                  title={convertOverTimeChartData.title}
                  xLabels={convertOverTimeChartData.xLabels}
                  series={convertOverTimeChartData.series}
                  dataMatrix={convertOverTimeChartData.dataMatrix}
                  yTicks={convertOverTimeChartData.yTicks}
                />
              )}
          </Grid>
        </Grid>

        <Grid container sx={{ mt: "34px !important" }}>
          <Grid item xs={12}>
            {convertOTIFFailsWarehouseDynamic?.xLabels?.length > 0 &&
              convertOTIFFailsWarehouseDynamic?.series?.length > 0 &&
              convertOTIFFailsWarehouseDynamic?.dataMatrix?.length > 0 && (

                <Barcharts
                  title={convertOTIFFailsWarehouseDynamic.title}
                  xLabels={convertOTIFFailsWarehouseDynamic.xLabels}
                  series={convertOTIFFailsWarehouseDynamic.series}
                  dataMatrix={convertOTIFFailsWarehouseDynamic.dataMatrix}
                  yTicks={[0, 400, 800, 1200, 1600, 2000]}   // adjust scale for fail counts
                  yAxisLabel="OTIF Fail Count"
                  rounded={true}
                />

              )}
          </Grid>
        </Grid>

        <Grid container sx={{ mt: "34px !important" }}>
          <Grid item xs={12}>
            {convertCasesInStockVsOrderedChart?.xLabels?.length > 0 &&
              convertCasesInStockVsOrderedChart?.series?.length > 0 &&
              convertCasesInStockVsOrderedChart?.dataMatrix?.length > 0 && (
                <LineChartsale
                  title={convertCasesInStockVsOrderedChart.title}
                  xLabels={convertCasesInStockVsOrderedChart.xLabels}
                  series={convertCasesInStockVsOrderedChart.series}
                  dataMatrix={convertCasesInStockVsOrderedChart.dataMatrix}
                  yTicks={convertCasesInStockVsOrderedChart.yTicks}
                  fillColor="#62BBFF"
                  yAxisLabel="Dollars ($M)"
                />
              )}
          </Grid>
        </Grid>
        <Grid container sx={{ mt: "34px !important" }}>
          <Grid item xs={12}>
            {convertCasesForecastLineChart?.xLabels?.length > 0 &&
              convertCasesForecastLineChart?.series?.length > 0 &&
              convertCasesForecastLineChart?.dataMatrix?.actual?.length > 0 &&
              convertCasesForecastLineChart?.dataMatrix?.forecast?.length > 0 && (
                <LineChartSubBrand
                  title={convertCasesForecastLineChart.title}
                  xLabels={convertCasesForecastLineChart.xLabels}
                  series={convertCasesForecastLineChart.series}
                  dataMatrix={convertCasesForecastLineChart.dataMatrix}
                  yTicks={convertCasesForecastLineChart?.yTicks}
                />
              )}
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mt: '34px !important' }}>
          {/* LEFT CARD - Top 10 Backordered SKUs */}
          <Grid item xs={12} md={6} sx={{
            pt: { xs: "0px !important", sm: "0px !important", }
          }}>
            {convertTopSkuBackorderChartData && (
              <TopSkuBarChart
                title={convertTopSkuBackorderChartData.title}
                xLabels={convertTopSkuBackorderChartData.xLabels}
                series={convertTopSkuBackorderChartData.series}
                dataMatrix={convertTopSkuBackorderChartData.dataMatrix}
                xTicks={convertTopSkuBackorderChartData.xTicks || [0, 500, 1000, 1500, 2000]}
              />
            )}
          </Grid>

          {/* RIGHT CARD - Top 10 Stocked SKUs */}
          <Grid item xs={12} md={6} sx={{
            pt: { xs: "34px !important", sm: "34px !important", md: '0px !important' }
          }}>
            {convertTopStockedSkuChartData && (
              <TopSkuBarChart
                title={convertTopStockedSkuChartData.title}
                xLabels={convertTopStockedSkuChartData.xLabels}
                series={convertTopStockedSkuChartData.series}
                dataMatrix={convertTopStockedSkuChartData.dataMatrix}
                xTicks={convertTopStockedSkuChartData.xTicks || [0, 1000, 2000, 3000, 4000]}
              />
            )}
          </Grid>
        </Grid>

      </Box>



    </Box>


  );
};

export default OperationsDashboard;